import React, { useEffect, useState, useMemo } from "react";
import { NetworkCalls } from "../../NetworkCalls";
import { BoughtGuestCard } from "../../dtos/BoughtGuestCard";
import LoadingPageIndicator from "../../components/LoadingPageIndicator";
import uuid from "react-uuid";
import TimestampFormat from "../../utils/TimestampFormat";
import { Dropdown } from "react-bootstrap";

export default function Payments() {
  const [loading, isLoading] = useState<boolean>(false);
  const [displayDialog, setDisplayDialog] = useState<boolean>(false);
  const [refundLoading, setRefundLoading] = useState<String>();
  const [boughtGuestCards, setBoughtGuestCards] = useState<BoughtGuestCard[]>(
    []
  );
  const [guestCardRefund, setGuestCardRefund] = useState<BoughtGuestCard>();
  const [error, setError] = useState<String>();

  // Filter
  const [selectedYear, setSelectedYear] = useState<number>(-1);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  // Search
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    isLoading(true);
    NetworkCalls.getBoughtGuestCards()
      .then((result) => {
        setBoughtGuestCards(result);
      })
      .finally(() => isLoading(false));
  }, []);

  // Extract unique years from data
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    boughtGuestCards.forEach((card) => {
      if (card.dateBought) {
        const year = new Date(card.dateBought).getFullYear();
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [boughtGuestCards]);

  // Set default year when data loads
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === -1) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Filter by year and search query
  const filteredData = useMemo(() => {
    return boughtGuestCards.filter((item) => {
      // Year filter
      if (selectedYear !== -1) {
        const itemYear = new Date(item.dateBought).getFullYear();
        if (itemYear !== selectedYear) return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const firstName = item.firstName?.toLowerCase() || '';
        const lastName = item.lastName?.toLowerCase() || '';
        const email = item.email?.toLowerCase() || '';
        const stripeId = item.stripeIntentId?.toLowerCase() || '';
        const guestCardName = item.guestCardId?.description?.toLowerCase() || '';
        const price = item.price?.toString() || '';
        return firstName.includes(query) || lastName.includes(query) ||
          email.includes(query) || stripeId.includes(query) || guestCardName.includes(query) ||
          price.includes(query);
      }
      return true;
    });
  }, [boughtGuestCards, selectedYear, searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  function showContent() {
    return (
      <div className="container-fluid px-0" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
        <div className="row g-0 h-100">
          {/* Filter Sidebar */}
          <div className="col-12 col-md-3 col-lg-2 bg-light border-end h-100" style={{ overflowY: 'auto' }}>
            <div className="p-3">
              <h5 className="mb-3">Filter</h5>

              <div className="mb-4">
                <label htmlFor="yearControl" className="form-label fw-semibold">Jahr</label>
                <select
                  id="yearControl"
                  className="form-select"
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={-1}>Alle Jahre</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-12 col-md-9 col-lg-10 h-100" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="p-3 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
              {/* Error Alert */}
              {error && (
                <div className="alert alert-danger mb-3" role="alert">
                  {error}
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="m-0">Gekaufte Gastkarten {selectedYear !== -1 ? selectedYear : ''}</h4>
                <div className="d-flex align-items-center gap-3">
                  <div className="input-group input-group-sm" style={{ width: '250px' }}>
                    <span className="input-group-text bg-white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Suche..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                    {searchQuery && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => {
                          setSearchQuery("");
                          setCurrentPage(1);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10 pro Seite</option>
                    <option value={25}>25 pro Seite</option>
                    <option value={50}>50 pro Seite</option>
                    <option value={100}>100 pro Seite</option>
                  </select>
                </div>
              </div>

              <div className="card shadow-sm flex-grow-1" style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>
                  {showBoughtGuestCardsTable()}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-2">
                    <span className="text-muted small">
                      Zeige {startIndex + 1} - {Math.min(endIndex, filteredData.length)} von {filteredData.length}
                    </span>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          >
                            «
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            ›
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            »
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Refund Dialog */}
        <div
          className="modal"
          style={{ display: displayDialog ? "block" : "none" }}
          role="dialog"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  Gastkarte erstatten
                </h5>
              </div>
              <div className="modal-body">
                Soll die Gastkarte von {guestCardRefund?.firstName}{" "}
                {guestCardRefund?.lastName} zum Preis von{" "}
                {guestCardRefund?.price}€ wirklich erstattet werden?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDisplayDialog(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => refund()}
                >
                  Gastkarte erstatten
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function showBoughtGuestCardsTable() {
    return (
      <table className="table table-hover table-striped mb-0">
        <thead className="table-light sticky-top">
          <tr key={uuid()}>
            <th scope="col">Gastkartennummer</th>
            <th scope="col">Kaufdatum</th>
            <th scope="col">Name</th>
            <th scope="col">Gültigkeit</th>
            <th scope="col">E-Mail</th>
            <th scope="col">Preis</th>
            <th scope="col">Gastkarte</th>
            <th scope="col">Status</th>
            <th scope="col" style={{ width: '60px' }}></th>
          </tr>
        </thead>
        <tbody id="table-content">{createRows()}</tbody>
      </table>
    );
  }

  function createRows() {
    return paginatedData.map((elem) => {
      return (
        <tr key={uuid()} className="user-select-auto">
          <td>
            <a
              href={`${process.env.REACT_APP_BASE_URL}/guestcard/pdf/${elem.stripeIntentId}`}
              target="_blank"
              rel="noreferrer"
            >
              {elem.stripeIntentId}
            </a>
          </td>
          <td>{TimestampFormat.toDateTimeReadableFormat(elem.dateBought)}</td>
          <td>
            {elem.firstName} {elem.lastName}
          </td>
          <td>
            {TimestampFormat.toDateTimeReadableFormat(elem.startGuestCardDate)}{" "}
            - {TimestampFormat.toDateTimeReadableFormat(elem.endGuestCardDate)}
          </td>
          <td>{elem.email}</td>
          <td>{elem.price}€</td>
          <td>{elem.guestCardId.description}</td>
          <td>{convertStatus(elem.status)}</td>
          <td>
            {elem.status !== "REFUNDED" && (
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="link"
                  className="btn btn-link p-1 text-decoration-none text-primary fs-5 lh-1"
                  style={{ fontSize: '1.5rem' }}
                >
                  ⋮
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ backgroundColor: "#fff", zIndex: 1060 }}>
                  <Dropdown.Item
                    className="text-warning"
                    onClick={() => askForRefund(elem)}
                    disabled={refundLoading === elem.transactionId}
                  >
                    {refundLoading === elem.transactionId ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="icon icon-tabler icon-tabler-arrow-back-up me-2"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M9 14l-4 -4l4 -4"></path>
                          <path d="M5 10h11a4 4 0 1 1 0 8h-1"></path>
                        </svg>
                    )}
                    Erstatten
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </td>
        </tr>
      );
    });
  }

  function askForRefund(guestCard: BoughtGuestCard) {
    setDisplayDialog(true);
    setGuestCardRefund(guestCard);
  }

  function refund() {
    setDisplayDialog(false);
    setError(undefined);
    if (guestCardRefund) {
      setRefundLoading(guestCardRefund.transactionId);
      NetworkCalls.refundGuestCard(guestCardRefund.transactionId)
        .then((result) => {
          boughtGuestCards.forEach((boughtGuestCard) => {
            if (
              boughtGuestCard.transactionId === guestCardRefund.transactionId
            ) {
              boughtGuestCard.status = "REFUNDED";
            }
          });
        })
        .catch((error) => setError("Gastkarte konnte nicht erstattet werden."))
        .finally(() => {
          setRefundLoading(undefined);
          setGuestCardRefund(undefined);
        });
    } else {
      setError("Gastkarte konnte nicht erstattet werden.");
    }
  }

  function convertStatus(status: string) {
    if (status === "REFUNDED") {
      return "Erstattet";
    }

    if (
      status === "payment_intent.succeeded" ||
      status === "charge.succeeded"
    ) {
      return "Zahlung erfolgt";
    }

    if (status === "charge.pending") {
      return "Zahlung ausstehend";
    }
  }

  return <div>{loading ? <LoadingPageIndicator /> : showContent()}</div>;
}
